import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, MessageSquare, Quote, User } from "lucide-react";

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      // Ensure we hit the correct endpoint
      const res = await axios.get(`${API_URL}/admin/public/testimonials`);

      // Filter for active status
      const data = res.data.testimonials || res.data;
      if (Array.isArray(data)) {
        const active = data.filter(t => t.status === true);
        setTestimonials(active);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching testimonials:", err);
      setError("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-indigo-500 selection:text-white pb-20">

      {/* HERO SECTION */}
      <div className="relative bg-slate-900 pt-32 pb-48 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-[100px] animate-pulse delay-700"></div>
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        </div>

        <div className="relative container mx-auto px-6 text-center z-10">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block py-1 px-3 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-semibold text-sm mb-6 uppercase tracking-wider"
          >
            <Star size={14} className="inline mr-2 -mt-0.5 text-yellow-400" />
            Client Success Stories
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-8 tracking-tight leading-none"
          >
            Voices of <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-pink-400 to-indigo-400">Trust</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Don't just take our word for it. Hear from the visionary leaders and businesses we've had the privilege to partner with.
          </motion.p>
        </div>

        {/* Abstract Wave at Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="fill-slate-50" viewBox="0 0 1440 120">
            <path d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </div>

      {/* REVIEWS GRID */}
      <div className="container mx-auto px-6 -mt-32 relative z-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-xl rounded-3xl border border-white/50 shadow-xl">
            <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-slate-500 font-semibold animate-pulse">Loading Stories...</p>
          </div>
        ) : error ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
            <p className="text-rose-500 text-lg font-bold">{error}</p>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
            <div className="inline-block p-4 rounded-full bg-slate-100 mb-4">
              <MessageSquare size={32} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">No testimonials available at the moment.</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8"
          >
            {testimonials.map((item) => (
              <motion.div
                key={item._id}
                variants={itemVariants}
                className="break-inside-avoid bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/50 border border-slate-100 relative hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 transform hover:-translate-y-2"
              >
                <Quote size={40} className="text-indigo-100 absolute top-6 right-6" />

                {/* Review Text */}
                <p className="text-slate-600 leading-relaxed mb-6 italic relative z-10">
                  "{item.review}"
                </p>

                {/* Rating */}
                <div className="flex text-yellow-400 gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill={i < item.rating ? "currentColor" : "none"} className={i < item.rating ? "" : "text-slate-200"} />
                  ))}
                </div>

                {/* Profile */}
                <div className="flex items-center gap-4 pt-6 border-t border-slate-100">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                    {item.image ? (
                      <img src={`${API_URL}/uploads/${item.image}`} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400"><User size={20} /></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">{item.name}</h4>
                    <p className="text-xs text-indigo-500 font-bold uppercase tracking-wide">Client</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

    </div>
  );
};

export default TestimonialsPage;